import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useChatWidget } from '../contexts/ChatWidgetContext';
import ChatService from '../services/chatService';
import supabase from '../utils/supabase';
import Icon from './AppIcon';
import Button from './ui/Button';

// Separate component for message list to prevent unnecessary re-renders
const MessageList = React.memo(({ messages, loading, containerRef, onScroll }) => {
  return (
    <div 
      ref={containerRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Icon name="Headphones" size={40} className="text-gray-300 mb-3" />
          <p className="text-sm text-text-muted text-center">
            Start a conversation with our support team. We're here to help!
          </p>
        </div>
      ) : (
        messages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'} animate-in fade-in slide-in-from-bottom-2`}
          >
            <div className="flex gap-2 max-w-xs">
              {msg.is_admin && msg.sender?.profile_picture && (
                <img
                  src={msg.sender.profile_picture}
                  alt={msg.sender.full_name}
                  loading="lazy"
                  className="w-6 h-6 rounded-full mt-1"
                />
              )}
              <div
                className={`px-4 py-2 rounded-lg text-sm break-words ${
                  msg.is_admin
                    ? 'bg-white text-text-primary border border-border'
                    : 'bg-primary text-white'
                }`}
              >
                {msg.message_text}
                <div className={`text-xs mt-1 ${msg.is_admin ? 'text-text-muted' : 'text-primary-100'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
});

MessageList.displayName = 'MessageList';

// Separate component for scroll button
const ScrollButton = React.memo(({ show, onClick }) => {
  if (!show) return null;
  
  return (
    <button
      onClick={onClick}
      className="absolute bottom-20 right-8 w-10 h-10 bg-primary/40 text-white rounded-full shadow-lg hover:shadow-xl hover:bg-primary/60 transition-all duration-200 flex items-center justify-center hover:scale-110 transform z-20"
      title="Scroll to bottom"
      aria-label="Scroll to bottom"
    >
      <Icon name="ArrowDown" size={20} />
    </button>
  );
});

ScrollButton.displayName = 'ScrollButton';

// Separate component for input area
const ChatInput = React.memo(({ 
  conversation, 
  newMessage, 
  onMessageChange, 
  onSendMessage, 
  onRateClick,
  loading 
}) => {
  if (conversation?.status === 'closed') {
    return null;
  }

  return (
    <div className="p-4 border-t border-border bg-white flex-shrink-0">
      <div className="flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={onMessageChange}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && onSendMessage()}
          placeholder="Type your message..."
          className="flex-1 px-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          disabled={loading}
        />
        <button
          onClick={onSendMessage}
          disabled={!newMessage.trim() || loading}
          className="p-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Send message"
          aria-label="Send message"
        >
          <Icon name="Send" size={18} />
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          onClick={onRateClick}
          className="text-xs text-primary hover:underline"
          title="Rate this conversation"
        >
          Rate conversation
        </button>
      </div>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

const LiveChatWidget = ({ defaultOpen = false, onOpenChange = null }) => {
  const { user, userProfile } = useAuth();
  const { isChatOpen, setIsChatOpen } = useChatWidget();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isHidden, setIsHidden] = useState(false); // Track if user has closed the bubble
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const initializingRef = useRef(false); // Prevent duplicate initialization
  const hasInitializedRef = useRef(false); // Track if we've initialized for this session

  const isOpen = defaultOpen ? isChatOpen || defaultOpen : isChatOpen;

  // Persist and restore isHidden state from localStorage
  useEffect(() => {
    // Load hidden state from localStorage on mount
    const savedHiddenState = localStorage.getItem('liveChatWidget_isHidden');
    if (savedHiddenState === 'true') {
      setIsHidden(true);
    }
  }, []);

  // Persist isHidden state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('liveChatWidget_isHidden', isHidden.toString());
  }, [isHidden]);

  // Memoize scroll to bottom function
  const scrollToBottom = useCallback((immediate = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: immediate ? 'auto' : 'smooth',
        block: 'end'
      });
    }
  }, []);

  // Debounced scroll handler for better performance
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    
    scrollTimeoutRef.current = setTimeout(() => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        setShowScrollButton(!isAtBottom);
      }
    }, 100);
  }, []);

  // Optimized messages effect - removed excessive logging
  useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight <= clientHeight || scrollHeight - scrollTop - clientHeight < 100;
      
      if (isAtBottom) {
        setTimeout(() => scrollToBottom(), 100);
      }
    }
  }, [messages, scrollToBottom]);

  // Clear unread count when widget opens
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      setUnreadCount(0);
    }
  }, [isOpen, unreadCount]);

  // Chat open effect - scroll to bottom
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      setTimeout(() => scrollToBottom(true), 150);
    }
  }, [isOpen, scrollToBottom]);

  // Load messages
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const result = await ChatService.getConversationMessages(conversationId);
      if (result.success) {
        setMessages(result.data || []);
        
        if (userProfile) {
          // Mark all messages as read first
          await ChatService.markConversationAsRead(conversationId, userProfile.id);
          // Set unread count to 0 since we just marked as read
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }, [userProfile]);

  // Check for existing active conversations without creating new ones
  const checkExistingConversation = useCallback(async () => {
    if (!user || !userProfile) return;
    
    if (initializingRef.current) {
      console.log('[LiveChat] Already checking, skipping...');
      return;
    }

    try {
      initializingRef.current = true;

      console.log('[LiveChat] Checking for existing conversation for user:', userProfile.id);

      const result = await ChatService.getUserConversations(userProfile.id);
      
      if (result.success && result.data?.length > 0) {
        // Only look for open or in_progress conversations
        const activeConv = result.data.find(c => 
          c.status === 'open' || c.status === 'in_progress'
        );
        if (activeConv) {
          console.log('[LiveChat] Found existing active conversation:', activeConv.id);
          setConversation(activeConv);
          await loadMessages(activeConv.id);
          hasInitializedRef.current = true;
        } else {
          console.log('[LiveChat] No active conversation found');
          hasInitializedRef.current = true;
        }
      } else {
        console.log('[LiveChat] No conversations found');
        hasInitializedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to check for existing conversation:', error);
    } finally {
      initializingRef.current = false;
    }
  }, [user, userProfile, loadMessages]);

  // Initialize chat (create new conversation if needed)
  const initializeChat = useCallback(async () => {
    if (!user || !userProfile) return;
    
    // If we already have a conversation, just load it
    if (conversation) {
      console.log('[LiveChat] Conversation already exists');
      return;
    }
    
    // Prevent duplicate initialization
    if (initializingRef.current) {
      console.log('[LiveChat] Already initializing, skipping...');
      return;
    }

    try {
      initializingRef.current = true;
      setLoading(true);

      console.log('[LiveChat] Starting initialization for user:', userProfile.id);

      const result = await ChatService.getUserConversations(userProfile.id);
      
      if (result.success && result.data?.length > 0) {
        // Only look for open or in_progress conversations
        // Resolved and closed conversations should not be reopened
        const activeConv = result.data.find(c => 
          c.status === 'open' || c.status === 'in_progress'
        );
        if (activeConv) {
          console.log('[LiveChat] Found existing active conversation:', activeConv.id);
          setConversation(activeConv);
          await loadMessages(activeConv.id);
          hasInitializedRef.current = true;
          return;
        }
      }

      console.log('[LiveChat] Creating new conversation...');
      const newConvResult = await ChatService.startConversation(
        userProfile.id,
        'Live Chat Support',
        'general',
        'normal'
      );
      
      if (newConvResult.success && newConvResult.data) {
        console.log('[LiveChat] New conversation created:', newConvResult.data.id);
        setConversation(newConvResult.data);
        setMessages([]);
        hasInitializedRef.current = true;
      } else {
        console.error('[LiveChat] Failed to create conversation:', newConvResult.error);
        alert(`Failed to start chat: ${newConvResult.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      alert(`Error starting chat: ${error.message}`);
    } finally {
      setLoading(false);
      initializingRef.current = false;
    }
  }, [user, userProfile, loadMessages, conversation]);

  // Check for existing conversations on mount (don't create new ones)
  useEffect(() => {
    if (!hasInitializedRef.current && user && userProfile && !initializingRef.current) {
      console.log('[LiveChat] Component mounted, checking for existing conversations...');
      checkExistingConversation();
    }
  }, [user, userProfile, checkExistingConversation]);

  // Initialize chat when widget is opened
  useEffect(() => {
    if (isOpen && !conversation && !initializingRef.current && user && userProfile) {
      console.log('[LiveChat] Widget opened without conversation, initializing...');
      initializeChat();
    }
  }, [isOpen, conversation, user, userProfile, initializeChat]);

  // Real-time subscription with proper error handling and reconnection
  useEffect(() => {
    if (!conversation?.id || !userProfile?.id) return;

    const conversationId = conversation.id;
    const userId = userProfile.id;
    let isSubscribed = true;

    console.log('[LiveChat] Setting up real-time subscription for conversation:', conversationId);

    const channel = supabase
      .channel(`user-chat:${conversationId}:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          if (!isSubscribed) return;
          
          console.log('[LiveChat] New message received via realtime:', payload.new?.id);
          
          // Add a small delay to ensure DB consistency
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const result = await ChatService.getConversationMessages(conversationId);
          if (result.success && isSubscribed) {
            setMessages(result.data || []);
            
            // If message is from admin, mark as read if chat is open
            if (payload.new?.sender_id !== userId && payload.new?.is_admin) {
              console.log('[LiveChat] Admin message received');
              
              // Show bubble again if it was hidden
              if (isHidden) {
                console.log('[LiveChat] Admin message received, showing bubble again');
                setIsHidden(false);
                // Also clear from localStorage so it stays visible
                localStorage.removeItem('liveChatWidget_isHidden');
              }
              
              // If chat is closed, open it and show unread badge
              if (!isChatOpen) {
                console.log('[LiveChat] Opening chat widget');
                setIsChatOpen(true);
                if (onOpenChange) onOpenChange(true);
                setUnreadCount(1);
              } else {
                // Chat is open, mark as read immediately
                await ChatService.markConversationAsRead(conversationId, userId);
                setUnreadCount(0);
              }
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_conversations',
          filter: `id=eq.${conversationId}`
        },
        async (payload) => {
          if (!isSubscribed) return;
          
          console.log('[LiveChat] Conversation updated:', payload.new?.status);
          
          // Update conversation status if changed
          if (payload.new) {
            setConversation(prev => ({ ...prev, ...payload.new }));
          }
        }
      )
      .subscribe((status, err) => {
        console.log('[LiveChat] Subscription status:', status, err || '');
        
        if (status === 'SUBSCRIBED') {
          // Refresh messages on successful subscription to catch any missed messages
          loadMessages(conversationId);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('[LiveChat] Subscription error, will retry...');
        }
      });

    return () => {
      isSubscribed = false;
      console.log('[LiveChat] Cleaning up subscription for conversation:', conversationId);
      supabase.removeChannel(channel);
    };
  }, [conversation?.id, userProfile?.id, loadMessages, isChatOpen, onOpenChange, setIsChatOpen, isHidden]);

  // Memoized handlers
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !user || !userProfile || !conversation) return;

    // Prevent sending messages to resolved or closed conversations
    if (conversation.status === 'resolved' || conversation.status === 'closed') {
      alert('This conversation has been closed. Please start a new conversation.');
      setConversation(null);
      setMessages([]);
      hasInitializedRef.current = false; // Reset to allow new conversation
      return;
    }

    const messageContent = newMessage.trim();
    const conversationId = conversation.id;
    
    // Clear input immediately for better UX
    setNewMessage('');
    
    // Create optimistic message for immediate display
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: userProfile.id,
      message_text: messageContent,
      is_admin: false,
      created_at: new Date().toISOString(),
      sender: {
        id: userProfile.id,
        full_name: userProfile.full_name,
        profile_picture: userProfile.profile_picture
      }
    };
    
    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const result = await ChatService.sendMessage(
        conversationId,
        userProfile.id,
        messageContent,
        false
      );

      if (result.success) {
        // Reload messages to get the real message with correct ID
        // Small delay to ensure DB has committed the message
        setTimeout(async () => {
          await loadMessages(conversationId);
        }, 200);
      } else {
        // Remove optimistic message on failure
        setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        setNewMessage(messageContent); // Restore the message
        console.error('Failed to send message:', result.error);
        alert(`Error sending message: ${result.error}`);
      }
    } catch (error) {
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setNewMessage(messageContent); // Restore the message
      console.error('Failed to send message:', error);
      alert(`Error: ${error.message}`);
    }
  }, [newMessage, conversation, user, userProfile, loadMessages]);

  const handleRateConversation = useCallback(async (ratingValue) => {
    if (!conversation) return;

    try {
      const result = await ChatService.rateConversation(conversation.id, ratingValue);
      if (result.success) {
        setRating(ratingValue);
        setShowRating(false);
        setTimeout(() => {
          setIsChatOpen(false);
          if (onOpenChange) onOpenChange(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to rate conversation:', error);
    }
  }, [conversation, onOpenChange, setIsChatOpen]);

  const handleToggleWidget = useCallback(() => {
    const newState = !isOpen;
    setIsChatOpen(newState);
    
    // Only initialize chat (create conversation) when user opens the widget
    if (newState && !conversation) {
      initializeChat();
    }
    
    if (onOpenChange) {
      onOpenChange(newState);
    }
  }, [isOpen, conversation, initializeChat, setIsChatOpen, onOpenChange]);

  const handleMessageChange = useCallback((e) => {
    setNewMessage(e.target.value);
  }, []);

  const handleRateClick = useCallback(() => {
    setShowRating(true);
  }, []);

  const handleCloseBubble = useCallback((e) => {
    e.stopPropagation();
    setIsHidden(true);
  }, []);

  // Memoize unread badge
  const hasUnread = useMemo(() => unreadCount > 0, [unreadCount]);

  // Check if conversation is active (open or in_progress)
  const isConversationActive = useMemo(() => {
    return conversation && (conversation.status === 'open' || conversation.status === 'in_progress');
  }, [conversation]);

  // Only show widget for regular users (VA/employer), not for admins or moderators
  if (!user || !userProfile) return null;
  
  if (userProfile.role === 'admin' || userProfile.role === 'moderator') {
    console.log('[LiveChatWidget] Hiding widget for', userProfile.role, 'user');
    return null;
  }

  return (
    <>
      {/* Chat Widget Button - Show only if conversation is active, widget is closed, and not hidden */}
      {!isOpen && isConversationActive && !isHidden && (
        <div className="fixed bottom-20 md:bottom-6 right-6 z-40">
          <button
            onClick={handleToggleWidget}
            className={`w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-110 transform ${
              hasUnread ? 'animate-pulse ring-2 ring-primary ring-offset-2' : ''
            }`}
            title="Open live chat support"
            aria-label="Open live chat support"
          >
            <Icon name="Headphones" size={24} />
            {hasUnread && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          
          {/* Close Button */}
          <button
            onClick={handleCloseBubble}
            className="absolute -top-2 -left-2 w-6 h-6 bg-white hover:bg-gray-100 text-gray-700 rounded-full shadow-md flex items-center justify-center transition-all duration-200 hover:scale-110"
            aria-label="Close chat bubble"
            title="Close chat bubble"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Chat Window - Show if widget is open */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Icon name="Headphones" size={20} className="opacity-90" />
              <div>
                <h3 className="font-semibold">Live Chat Support</h3>
                <p className="text-xs text-primary-100">We typically reply in minutes</p>
              </div>
            </div>
            <button
              onClick={handleToggleWidget}
              className="p-1 hover:bg-primary-600 rounded-lg transition-colors"
              title="Close chat"
              aria-label="Close chat"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto relative">
            <MessageList
              messages={messages}
              loading={loading}
              containerRef={messagesContainerRef}
              onScroll={handleScroll}
            />
            
            {/* Scroll End Anchor */}
            <div ref={messagesEndRef} />
            
            {/* Scroll to Bottom Button */}
            <ScrollButton 
              show={showScrollButton && messages.length > 0} 
              onClick={() => scrollToBottom()} 
            />
          </div>

          {/* Rating Section */}
          {showRating && (
            <div className="px-4 py-4 bg-blue-50 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-text-primary">
                  How helpful was this conversation?
                </p>
                <button
                  onClick={() => setShowRating(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  title="Go back to chat"
                  aria-label="Go back to chat"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => handleRateConversation(star)}
                    className={`p-2 rounded-lg transition-colors ${
                      star <= rating
                        ? 'bg-yellow-400 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    title={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  >
                    <Icon name="Star" size={18} fill={star <= rating ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input or Closed State */}
          {conversation?.status === 'closed' ? (
            <div className="p-4 border-t border-border bg-gray-50 text-center">
              <p className="text-sm text-text-muted mb-3">
                This conversation has been closed.
              </p>
              <button
                onClick={() => {
                  // Reset all state for fresh initialization
                  setConversation(null);
                  setMessages([]);
                  setNewMessage('');
                  setUnreadCount(0);
                  hasInitializedRef.current = false;
                  setIsHidden(false); // Show bubble again when starting new conversation
                  // Close the widget to trigger clean initialization
                  setIsChatOpen(false);
                  if (onOpenChange) onOpenChange(false);
                }}
                className="text-sm text-primary hover:underline font-medium"
              >
                Start a new conversation
              </button>
            </div>
          ) : !showRating && (
            <ChatInput
              conversation={conversation}
              newMessage={newMessage}
              onMessageChange={handleMessageChange}
              onSendMessage={handleSendMessage}
              onRateClick={handleRateClick}
              loading={loading}
            />
          )}
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
